import React, { useState } from "react"
import styled from "styled-components"
import { navigate } from "gatsby-link"

// Custom hooks
import { useLocale } from "../hooks/useLocale"

// Styling
import mq from "../styles/breakpoints"
import textStyles from "../styles/textStyles"
import colors, { shadows } from "../styles/colors"

// Components
import ContentWrapper from "./ContentWrapper"
import RequestDemoForm, { IRequestVariant } from "./forms/RequestDemoForm"
import Cross from "./icons/Cross"

// Interfaces
import { IDemoPage } from "../prismic-types/request-demo-page"
import TelephoneIcon from "./icons/Telephone"
import EnvelopeIcon from "./icons/Envelope"

// Types
interface IProps {
  title: string
  text: string
  variant: IRequestVariant
  closeModal?: () => void
  buttonLabel?: string
  download?: string
  onSuccess?: () => void
  labels: IDemoPage["labels"]
  phoneTitle?: string
  phoneDescription?: string
  phoneNumber?: string
}

const RequestDemo: React.FC<IProps> = ({
  title,
  text,
  variant,
  closeModal,
  buttonLabel,
  download,
  onSuccess,
  labels,
  phoneTitle,
  phoneDescription,
  phoneNumber,
}) => {
  const [complete, setComplete] = useState<boolean>(false)
  const { isDutch } = useLocale()
  return (
    <Container>
      <StyledContentWrapper>
        <Content>
          <CloseButton onClick={closeModal ? closeModal : () => navigate(-1)}>
            <Cross color={colors.text} width={20} height={20} />
          </CloseButton>
          {(!complete || (complete && variant === "contact")) && (
            <TextSection>
              {variant === "contact" && (
                <TelephoneIcon
                  color={colors.purpleLight}
                  width={32}
                  height={32}
                />
              )}
              {(!complete || (complete && variant === "contact")) && (
                <div>
                  <Title>{phoneTitle || title}</Title>
                  <Text
                    dangerouslySetInnerHTML={{
                      __html: phoneDescription || text,
                    }}
                  />
                  {/* TODO: Please make phone as callable with anchor and align this with phone description above in same line */}
                  {phoneNumber && (
                    <PhoneNumber href={`tel:${phoneNumber}`}>
                      {phoneNumber}
                    </PhoneNumber>
                  )}
                </div>
              )}
            </TextSection>
          )}
          {!complete && variant === "contact" && (
            <>
              <hr
                style={{
                  marginBottom: 0,
                  color: colors.main["600"],
                }}
              />
              <TextSection>
                <EnvelopeIcon
                  color={colors.purpleLight}
                  width={32}
                  height={32}
                />
                <div>
                  <Title>{title}</Title>
                  <Text
                    dangerouslySetInnerHTML={{
                      __html: text,
                    }}
                  />
                </div>
              </TextSection>
            </>
          )}
          <RequestDemoForm
            labels={labels}
            variant={variant}
            download={download}
            buttonLabel={
              variant === "download"
                ? isDutch
                  ? "Stuur me de link"
                  : "Send me the link"
                : buttonLabel
            }
            onSuccess={onSuccess}
            complete={complete}
            setComplete={setComplete}
          />
        </Content>
      </StyledContentWrapper>
    </Container>
  )
}

const Container = styled.section`
  position: fixed;
  background-color: ${colors.dimLightPurple};
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  overflow-y: scroll;
`

const StyledContentWrapper = styled(ContentWrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 48px;
  padding-bottom: 48px;

  ${mq.from.S`
    height: 100%;
  `}

  ${mq.from.M`
    padding: 48px 15vw;
  `}

  ${mq.from.L`
    padding: 48px 25vw;
  `}

  ${mq.from.XL`
    max-width: unset;
  `}
`

const Content = styled.div`
  ${shadows.depthFour};
  background-color: ${colors.white};
  border-radius: 40px;
  padding: 40px 24px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;

  ${mq.from.M`
    padding: 48px;
  `}

  ${mq.from.XL`
    width: 656px;
    max-width: 1200px;
    margin: 0 auto;
  `}
`

const CloseButton = styled.div`
  position: absolute;
  top: 32px;
  right: 32px;
  cursor: pointer;
`

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;

  ${mq.from.XSM`
    flex-direction: row;
    text-align: left;
    align-items: center;
  `}

  > svg {
    min-width: 32px;
    min-height: 32px;
    margin-bottom: 8px;

    ${mq.from.M`
      margin-bottom: 0;
    `}
  }
`

const Title = styled.h3`
  ${textStyles.titleL};
  font-size: 32px;
  color: ${colors.purpleLight};

  ${mq.from.M`
    font-size: 32px;
    line-height: 1.12;
  `}
`

const Text = styled.div`
  color: ${colors.main["100"]};
  margin-bottom: 0;
  display: inline;

  > p {
    margin-bottom: 0;
    font-size: 14px;
    line-height: 20px;
    display: inline;
  }

  > a {
    color: ${colors.purpleLight};
  }
`

const PhoneNumber = styled.a`
  color: ${colors.purpleLight};
  text-decoration: none;
  display: inline;
  font-size: 14px;
  line-height: 20px;
  padding-left: 4px !important;

  &:hover {
    text-decoration: underline;
  }
`

export default RequestDemo
